import React, {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useState,
} from 'react';

import { useWizardContext } from 'Views/Components/Wizard/WizardContext';

import { IStudyPlanWizardData } from 'Views/Pages/Frontend/StudyPlanWizard/StudyPlanWizard';

import {
	buildRequirementTree,
} from 'Views/Pages/Frontend/StudyPlanWizard/RequirementTreeGeneration';

import {
	AvailableCourseFilters,
} from 'Views/Pages/Frontend/StudyPlanWizard/SearchAndFilterUtilities';

import useAsync from 'Hooks/useAsync';
import {
	IGeneratedPlanFeedback,
} from 'Queries/Hooks/StudyPlan/types';
import { useMounted } from 'Hooks/useMounted';
import useDebounce from 'Hooks/useDebounce';
import { AbortSignals } from 'Hooks/useAbortSignals';

import {
	CampusEntity, CourseEntity,
	CourseLevelEntity,
	CourseOfferingEntity,
	DisciplineEntity,
	FacultyEntity,
	PlanSelectionEntity,
	RequirementEntity,
	SchoolEntity,
	TermEntity,
} from 'Models/Entities';

import {
	DataIndex, indexByProperty, indexByPropertyArray,
} from 'Util/DataIndexUtils';
import { SearchAndFilterState }
	from 'Views/Pages/Frontend/StudyPlanWizard/CourseSelection/CourseSelectionFilterInput';
import { validateCourseSelection as validateCourseSelectionQuery } from 'Queries/REST/StudyPlan/StudyPlanRequests';
import { getCourseOfferingsForRequirements } from 'Queries/REST/CourseOffering/CourseOfferingRequests';

const emptyParentRequirementKey = 'none';

type RequirementIndex = ReturnType<typeof useRequirementIndexContext>;

const defaultSearchAndFilterStateState = {
	searchPhrase: '',
	selectedLevelId: '',
	selectedTermId: '',
	selectedFacultyId: '',
	selectedSchoolId: '',
	selectedDisciplineId: '',
	selectedLocationId: '',
};

const defaultAvailableFilters = {
	disciplines: [],
	faculties: [],
	levels: [],
	locations: [],
	schools: [],
	terms: [],
};

const useRequirementIndexContext = () => {
	const mounted = useMounted();
	const {
		data,
	} = useWizardContext<IStudyPlanWizardData>();

	const [
		requirementsByParentId, setRequirementsByParentId,
	] = useState<DataIndex<RequirementEntity[]>>({});

	const [planSelections, setPlanSelections] = useState<PlanSelectionEntity[]>([]);

	const components = useMemo(() => {
		return (requirementsByParentId[emptyParentRequirementKey] || [])
			.sort((a, b) => a.order - b.order);
	}, [requirementsByParentId]);

	const [coursesById, setCoursesById] = useState<DataIndex<CourseEntity>>({});

	const [
		courseOfferingsById, setCourseOfferingsById,
	] = useState<DataIndex<CourseOfferingEntity>>({});

	const [searchAndFilterState, setSearchAndFilterState] = useState<SearchAndFilterState>(
		{ ...defaultSearchAndFilterStateState },
	);

	const [currentComponentId, setCurrentComponentId] = useState<string>();

	const [availableCourseFilters, setAvailableCourseFilters] = useState<AvailableCourseFilters>(
		defaultAvailableFilters,
	);

	/**
	 * Complex Data Index
	 * Offering List keyed by requirement, then course
	 *
	 * This is so a given requirement can immedately retrieve
	 * all offerings for a given course
	 */
	const [
		courseOfferingIdByCourseIdByRequirementId, setCourseOfferingIdByRequirementByCoursedId,
	] = useState<DataIndex<DataIndex<string[]>>>({});

	const [
		requirementsById, setRequirementsById,
	] = useState<DataIndex<RequirementEntity>>({});

	const [generatedPlanFeedback, setGeneratedPlanFeedback] = useState<IGeneratedPlanFeedback>();

	const completedCourseIds = useMemo(() => {
		return planSelections.filter(course => course.completionStatus === 'COMPLETE')
			.map(course => course.courseId);
	}, [planSelections]);

	const duplicateCourseIds = useMemo(() => {
		// index selected by course id into an array
		const planSelectionsByCourseId = indexByPropertyArray(
			planSelections,
			planSelection => planSelection.courseId,
		);

		return Object.entries(planSelectionsByCourseId)
			.filter(([courseId, selections]) => selections.length > 1)
			.map(([courseId, selections]) => courseId);
	}, [planSelections]);

	const resetSearchAndFilterState = useCallback(() => {
		setSearchAndFilterState({ ...defaultSearchAndFilterStateState });
	}, []);

	// when current component changes, clear the search filters
	useEffect(() => {
		resetSearchAndFilterState();
	}, [currentComponentId, resetSearchAndFilterState]);

	const validateSelectionAndUpdateFeedback = useCallback(
		async (
			abortSignals: AbortSignals | undefined,
			studyPlanId: string,
			plannedCourseOfferingIds: string[],
			completedCourses: string[],
		) => {
			validateCourseSelectionQuery(
				studyPlanId,
				plannedCourseOfferingIds,
				completedCourses,
			)
				.then(response => {
					if (!mounted.current) {
						return;
					}
					setGeneratedPlanFeedback(response.data.feedback);
				})
				.catch(() => {});
		},
		[mounted],
	);

	const validateCourseSelectionDebounced = useDebounce(
		validateSelectionAndUpdateFeedback,
		[validateSelectionAndUpdateFeedback],
		1100,
	);

	// retrieve and set further required data based on the loaded study plan
	const createSelectionIndexes = async () => {
		if (!data?.studyPlan) {
			return;
		}

		// retrieve offerings based on requirements directly
		// assigned to study plan
		const studyPlanRequirementIds = data.studyPlan.studyPlanRequirements
			.map(requirementJoin => requirementJoin.studyPlanRequirementId);

		// query for latest program structure
		const programOfferingsResponse = await getCourseOfferingsForRequirements(studyPlanRequirementIds);

		// exit if component is no longer mounted
		if (!mounted.current || !programOfferingsResponse) {
			return;
		}

		const {
			availableCourseFilters: availableCourseFiltersAttributes,
			courseOfferings: courseOfferingsAttributes,
			requirements: requirementsAttributes,
		} = programOfferingsResponse;

		const courseOfferings = courseOfferingsAttributes
			.map(offeringAttributes => new CourseOfferingEntity(offeringAttributes));

		const courses = courseOfferings.map(courseOffering => courseOffering.course);

		const requirements = requirementsAttributes
			.map(requirementAttributes => new RequirementEntity(requirementAttributes));

		// throw some errors if the api failed to return
		// required data
		if (!courseOfferings) {
			throw new Error('Offerings were not retrieved');
		}
		if (!requirements) {
			throw new Error('Requirements were not retrieved');
		}
		if (!availableCourseFilters) {
			throw new Error('Available filters were not retrieved');
		}

		setAvailableCourseFilters({
			levels: availableCourseFiltersAttributes.levels
				.map(level => new CourseLevelEntity(level)),
			terms: availableCourseFiltersAttributes.terms
				.map(term => new TermEntity(term)),
			faculties: availableCourseFiltersAttributes.faculties
				.map(faculty => new FacultyEntity(faculty)),
			schools: availableCourseFiltersAttributes.schools
				.map(school => new SchoolEntity(school)),
			disciplines: availableCourseFiltersAttributes.disciplines
				.map(discipline => new DisciplineEntity(discipline)),
			locations: availableCourseFiltersAttributes.locations
				.map(location => new CampusEntity(location)),
		});

		const offeringsByRequirementByCourse: DataIndex<DataIndex<string[]>> = {};

		const buildOfferingsByRequirementByCourseIndex = (offering: CourseOfferingEntity) => {
			const { programRequirementId, courseId } = offering;
			const existingOfferingsForRequirement = offeringsByRequirementByCourse[programRequirementId];

			// if no existing entry exists, create one
			if (!existingOfferingsForRequirement) {
				offeringsByRequirementByCourse[programRequirementId] = {
					[courseId]: [
						// eslint-disable-next-line max-len
						...(offeringsByRequirementByCourse[programRequirementId]?.[courseId] || []),
						offering.id,
					],
				};
			} else { // existing entry exists - add to it
				existingOfferingsForRequirement[courseId] = [
					// eslint-disable-next-line max-len
					...(offeringsByRequirementByCourse[programRequirementId][courseId] || []),
					offering.id,
				];
			}
		};

		const offeringsById = indexByProperty(
			courseOfferings,
			offering => {
				// while we're iterating to build this index, let's build another one!
				buildOfferingsByRequirementByCourseIndex(offering);
				// return data index
				return offering.id;
			},
		);

		const courseEntitiesById = indexByProperty(
			courses,
			course => course.id,
		);

		const programRequirementsById: DataIndex<RequirementEntity> = {};

		const indexedRequirementsByParentId = indexByPropertyArray(
			requirements,
			requirement => {
				programRequirementsById[requirement.id] = requirement;

				return requirement.parentRequirementId || emptyParentRequirementKey;
			},
		);

		setCourseOfferingIdByRequirementByCoursedId(offeringsByRequirementByCourse);
		setCourseOfferingsById(offeringsById);
		setCoursesById(courseEntitiesById);

		setRequirementsById(programRequirementsById);
		setRequirementsByParentId(indexedRequirementsByParentId);

		const foundComponents = indexedRequirementsByParentId[emptyParentRequirementKey];

		if (!foundComponents || !foundComponents.length) {
			throw new Error('No top-level requirements were found');
		}

		// order components according to configured data driven order property
		foundComponents
			.sort((a, b) => a.order - b.order);

		if (foundComponents.length === 0) {
			throw new Error('No components were found');
		}

		const planSelectionEntities = data.studyPlan
			.planSelections
			.map(planSelection => new PlanSelectionEntity(planSelection));

		setPlanSelections(planSelectionEntities);

		validateSelectionAndUpdateFeedback(
			undefined,
			data.studyPlan.id,
			data.studyPlan
				.getIncompletePlanSelections()
				.map(planSelection => planSelection.courseOfferingId!),
			data.studyPlan
				.getCompletedPlanSelections()
				.map(planSelection => planSelection.courseId),
		);

		if (currentComponentId === undefined) {
			// default to display the first component on load
			setCurrentComponentId(foundComponents[0].id);
		}
	};

	useAsync(
		createSelectionIndexes,
		[data?.studyPlan?.programStructureId, data?.studyPlan?.studyPlanRequirements],
	);

	const requirementTree = useMemo(() => {
		if (!components.length || !planSelections) {
			return {};
		}

		return buildRequirementTree(
			courseOfferingIdByCourseIdByRequirementId,
			components,
			requirementsByParentId,
			planSelections,
			coursesById,
			courseOfferingsById,
			searchAndFilterState,
		);
	}, [
		components,
		planSelections,
		courseOfferingIdByCourseIdByRequirementId,
		requirementsByParentId,
		coursesById,
		courseOfferingsById,
		searchAndFilterState,
	]);

	return {
		requirementsByParentId,
		setRequirementsByParentId,

		planSelections,
		setPlanSelections,

		completedCourseIds,
		duplicateCourseIds,

		components,

		courseOfferingsById,
		setCourseOfferingsById,

		coursesById,

		searchAndFilterState,
		setSearchAndFilterState,

		resetSearchAndFilterState,

		availableCourseFilters,
		setAvailableCourseFilters,

		currentComponentId,
		setCurrentComponentId,

		courseOfferingIdByCourseIdByRequirementId,
		setCourseOfferingIdByRequirementByCoursedId,

		requirementsById,
		setRequirementsById,

		generatedPlanFeedback,
		setGeneratedPlanFeedback,
		validateCourseSelection: validateCourseSelectionDebounced,
		requirementTree,
	};
};

const defaultRequirementIndexContext: RequirementIndex = {
	requirementsByParentId: {},
	setRequirementsByParentId: () => {},

	planSelections: [],
	setPlanSelections: () => {},

	completedCourseIds: [],
	duplicateCourseIds: [],

	components: [],

	courseOfferingsById: {},
	setCourseOfferingsById: () => {},

	coursesById: {},

	searchAndFilterState: { ...defaultSearchAndFilterStateState },
	setSearchAndFilterState: () => {},
	resetSearchAndFilterState: () => {},

	courseOfferingIdByCourseIdByRequirementId: {},
	setCourseOfferingIdByRequirementByCoursedId: () => {},

	requirementsById: {},
	setRequirementsById: () => {},

	availableCourseFilters: defaultAvailableFilters,
	setAvailableCourseFilters: () => {},

	currentComponentId: undefined,
	setCurrentComponentId: () => {},

	generatedPlanFeedback: undefined,
	setGeneratedPlanFeedback: () => {},

	validateCourseSelection: (async () => {}) as unknown as RequirementIndex['validateCourseSelection'],

	requirementTree: {},
};

export const RequirementIndexContext = createContext<RequirementIndex>(
	defaultRequirementIndexContext,
);

export const RequirementIndexProvider = ({ children }: { children: React.ReactNode }) => {
	const contextData = useRequirementIndexContext();
	return (
		<RequirementIndexContext.Provider value={contextData}>
			{children}
		</RequirementIndexContext.Provider>
	);
};

export const useRequirementIndex = () => useContext(RequirementIndexContext);
